import React, { useState } from 'react';

function MediaKit() {
  const [copiedText, setCopiedText] = useState('');

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const brandColors = [
    { name: 'Primary Navy', hex: '#1e3a8a', rgb: 'rgb(30, 58, 138)', usage: 'Primary brand color, headers, CTAs' },
    { name: 'Primary Red', hex: '#dc2626', rgb: 'rgb(220, 38, 38)', usage: 'Accent color, highlights, buttons' },
    { name: 'Dark Navy', hex: '#1e293b', rgb: 'rgb(30, 41, 59)', usage: 'Text, backgrounds' },
    { name: 'Gray', hex: '#64748b', rgb: 'rgb(100, 116, 139)', usage: 'Secondary text, borders' },
    { name: 'Light Gray', hex: '#f1f5f9', rgb: 'rgb(241, 245, 249)', usage: 'Backgrounds, cards' },
    { name: 'White', hex: '#ffffff', rgb: 'rgb(255, 255, 255)', usage: 'Text on dark backgrounds, cards' }
  ];

  const logoAssets = [
    {
      name: 'Navy Logo',
      preview: '/logo.webp',
      bgStyle: 'light',
      formats: [
        { type: 'WebP', path: '/logo.webp', filename: 'FCG-Logo-Navy.webp' },
        { type: 'PNG', path: '/logo.png', filename: 'FCG-Logo-Navy.png' },
        { type: 'JPG', path: '/logo.jpg', filename: 'FCG-Logo-Navy.jpg' },
        { type: 'SVG', path: '/logo.svg', filename: 'FCG-Logo-Navy.svg' }
      ]
    },
    {
      name: 'White Logo',
      preview: '/logo2.webp',
      bgStyle: 'dark',
      formats: [
        { type: 'WebP', path: '/logo2.webp', filename: 'FCG-Logo-White.webp' },
        { type: 'PNG', path: '/logo2.png', filename: 'FCG-Logo-White.png' },
        { type: 'JPG', path: '/logo2.jpg', filename: 'FCG-Logo-Navy.jpg' },
        { type: 'SVG', path: '/logo2.svg', filename: 'FCG-Logo-Navy.svg' }
      ]
    },
    {
      name: 'Navy Logo (with Text )',
      preview: '/logo1-text.webp',
      bgStyle: 'light',
      formats: [
        { type: 'WebP', path: '/logo1-text.webp', filename: 'FCG-Logo-Navy-Text.webp' },
        { type: 'PNG', path: '/logo1-text.png', filename: 'FCG-Logo-Navy-Text.png' },
        { type: 'SVG', path: '/logo1-text.svg', filename: 'FCG-Logo-Navy.svg' }
      ]
    },
    {
      name: 'Hero Background',
      preview: '/fcbghero1.webp',
      bgStyle: 'dark',
      formats: [
        { type: 'WebP', path: '/fcbghero1.webp', filename: 'FCG-bg.webp' }
      ]
    }
  ];

  const downloadAsset = (path, filename) => {
    const link = document.createElement('a');
    link.href = path;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    const allFiles = logoAssets.flatMap(asset => asset.formats);
    allFiles.forEach((file, index) => {
      setTimeout(() => {
        downloadAsset(file.path, file.filename);
      }, index * 300);
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Media</span>
                <span className="text-blue-900"> Kit</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Brand assets & style guide for Freedom Contracting Group</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={downloadAll}
                className="hidden sm:inline-flex items-center gap-2 bg-blue-900 text-white hover:bg-red-600 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                Download All Assets
              </button>
              <a
                href="/"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Back to Home"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Brand Overview */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-blue-900 via-gray-900 to-red-900 text-white rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Freedom Contracting Group</h2>
            <p className="text-xl text-gray-200 mb-6">Built on American Values</p>
            <p className="text-gray-300 leading-relaxed max-w-3xl">
              Our brand represents integrity, quality craftsmanship, and American values. We're proud to serve Chicago 
              and surrounding areas with commercial construction excellence. Our visual identity reflects our commitment 
              to building structures—and relationships—that stand the test of time.
            </p>
          </div>
        </section>

        

        {/* Logo Assets - MOVED BEFORE BRAND COLORS */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Logo Assets</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {logoAssets.map((logo, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div 
                  className={`h-48 flex items-center justify-center p-8 ${
                    logo.bgStyle === 'dark' ? 'bg-gradient-to-br from-blue-900 to-gray-900' : 'bg-gray-50'
                  }`}
                >
                  <img 
                    src={logo.preview} 
                    alt={logo.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-blue-900 text-lg">{logo.name}</h3>
                    <div className="flex items-center gap-2">
                      {logo.formats.map((format, fIndex) => (
                        <button
                          key={fIndex}
                          onClick={() => downloadAsset(format.path, format.filename)}
                          className="px-3 py-1.5 bg-blue-900 hover:bg-red-600 text-white text-sm font-medium rounded transition-colors"
                          title={`Download ${format.type}`}
                        >
                          {format.type}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Use on {logo.bgStyle} backgrounds
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Brand Colors */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Brand Colors</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandColors.map((color, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div 
                  className="h-32 relative group cursor-pointer"
                  style={{ backgroundColor: color.hex }}
                  onClick={() => copyToClipboard(color.hex, color.name)}
                >
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 text-white font-semibold">
                      Click to copy
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-3">{color.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">HEX:</span>
                      <button 
                        onClick={() => copyToClipboard(color.hex, `${color.name} HEX`)}
                        className="font-mono text-blue-900 hover:text-red-600 transition-colors"
                      >
                        {color.hex}
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">RGB:</span>
                      <button 
                        onClick={() => copyToClipboard(color.rgb, `${color.name} RGB`)}
                        className="font-mono text-blue-900 hover:text-red-600 transition-colors text-xs"
                      >
                        {color.rgb}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">{color.usage}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {copiedText && (
            <div className="fixed bottom-8 right-8 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl animate-fadeIn">
              âœ“ Copied {copiedText} to clipboard
            </div>
          )}
        </section>

        {/* Typography */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Typography</h2>
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">Primary Font</h3>
                <p className="text-4xl font-bold text-blue-900 mb-2">System Font Stack</p>
                <p className="text-gray-600 font-mono text-sm">
                  -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell'
                </p>
              </div>
              
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-6">Typography Scale</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Heading 1</p>
                    <p className="text-5xl font-bold text-blue-900">Building America's Future</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Heading 2</p>
                    <p className="text-4xl font-bold text-blue-900">One Project at a Time</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Heading 3</p>
                    <p className="text-2xl font-bold text-blue-900">Commercial Excellence</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Body Text</p>
                    <p className="text-base text-gray-700">
                      Comprehensive commercial construction solutions tailored to your needs with integrity and quality craftsmanship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
		{/* Leadership Team Headshots */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Leadership Team</h2>
          <p className="text-gray-600 mb-8 max-w-3xl">
            Professional headshots of our leadership team for press releases, media coverage, and partnership materials.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-80 overflow-hidden bg-gray-100">
                <img 
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" 
                  alt="Michael Anderson - Founder & CEO"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-blue-900 mb-1">Michael Anderson</h3>
                <p className="text-red-600 font-semibold mb-4">Founder & CEO</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => downloadAsset('https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&h=800&fit=crop', 'Michael-Anderson-Headshot-High.jpg')}
                    className="px-4 py-2 bg-blue-900 hover:bg-red-600 text-white text-sm font-medium rounded transition-colors"
                  >
                    High Res
                  </button>
                  <button
                    onClick={() => downloadAsset('https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop', 'Michael-Anderson-Headshot-Web.jpg')}
                    className="px-4 py-2 bg-blue-900 hover:bg-red-600 text-white text-sm font-medium rounded transition-colors"
                  >
                    Web Res
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Licensed General Contractor, PMP Certified, OSHA 30-Hour
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-80 overflow-hidden bg-gray-100">
                <img 
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop" 
                  alt="Sarah Martinez - VP of Operations & Safety Director"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-blue-900 mb-1">Sarah Martinez</h3>
                <p className="text-red-600 font-semibold mb-4">VP of Operations & Safety Director</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => downloadAsset('https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&h=800&fit=crop', 'Sarah-Martinez-Headshot-High.jpg')}
                    className="px-4 py-2 bg-blue-900 hover:bg-red-600 text-white text-sm font-medium rounded transition-colors"
                  >
                    High Res
                  </button>
                  <button
                    onClick={() => downloadAsset('https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop', 'Sarah-Martinez-Headshot-Web.jpg')}
                    className="px-4 py-2 bg-blue-900 hover:bg-red-600 text-white text-sm font-medium rounded transition-colors"
                  >
                    Web Res
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  OSHA 500 Trainer, CSP Certified Safety Professional, LEED AP
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Usage Guidelines */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Usage Guidelines</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-600 mb-3">Do's</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>Use the logo on clean backgrounds with sufficient contrast</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>Maintain proper clear space around the logo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>Use approved brand colors consistently</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>Scale the logo proportionally</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>Use navy logo on light backgrounds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>Use white logo on dark backgrounds</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-600 mb-3">Don'ts</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Don't distort or stretch the logo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Don't change the logo colors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Don't add effects, shadows, or outlines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Don't rotate or skew the logo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Don't place logo on busy backgrounds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Don't recreate or modify the logo</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-gradient-to-r from-blue-900 to-red-900 rounded-2xl p-8 md:p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Need More Assets?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            If you need additional brand assets or have questions about brand usage, please contact our marketing team.
          </p>
          <a 
            href="mailto:support@freedomcg.net"
            className="inline-flex items-center gap-2 bg-white text-blue-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-xl transition-all shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            Contact Marketing
          </a>
        </section>
      </div>
    </div>
  );
}

export default MediaKit;